import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import CustomModal from '../../../../../configuracion/components/customModal';
import PlanDeCuentaDropdown, { PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX } from '../../../../../../containers/PlanDeCuentaDropdown';
import { importAlertState } from '../../../../../../redux/actions/alert';
import { DevengarEntry } from '../../../../../../graphql/entries';
import EntryRowList from './EntryRowList';
import { formatMoneyWithCurrency } from '../../../../../../utils/formatter';
import {
  parseEntryLineAmount,
  buildAccrualLines,
  shouldInvertAccrualForAccount,
} from './devengarAccrualHelpers';
import './DevengarModal.scss';

/** Level-1 account key from accountKey or levels (e.g. "3", "4"). */
const getLevelOneKey = (line) => {
  const accountKey = String(line?.accountKey || '').trim();
  const levelKey = line?.levels?.one?.key;
  if (levelKey != null && String(levelKey).trim() !== '') {
    return String(levelKey).split('.')[0];
  }
  return accountKey ? accountKey.split('.')[0] : '';
};

/** P&L lines devengable: Gasto (3) or Ingreso (4). */
const isDevengableLine = (line) => {
  const accountKey = String(line?.accountKey || '').trim();
  if (/^3(\.|$)/.test(accountKey) || /^4(\.|$)/.test(accountKey)) return true;
  const levelOne = getLevelOneKey(line);
  return levelOne === '3' || levelOne === '4';
};

/** Net amount on a journal line (debit or credit side). */
const getLineAmount = (line) => {
  const debit = parseEntryLineAmount(line?.debit);
  const credit = parseEntryLineAmount(line?.credit);
  return Math.abs(debit - credit) || Math.max(debit, credit);
};

/** Sum of devengable-line amounts (optionally one selected account). */
const getDevengableTotal = (entry, selectedAccountKey) => {
  const devengableLines = (entry?.entries || []).filter(isDevengableLine);
  if (!devengableLines.length) return 0;

  const linesToSum = selectedAccountKey
    ? devengableLines.filter((l) => l.accountKey === selectedAccountKey)
    : devengableLines;

  const target = linesToSum.length ? linesToSum : devengableLines;
  return target.reduce((sum, l) => sum + getLineAmount(l), 0);
};

/** Default P&L line with the largest amount. */
const getDefaultResultadoAccount = (entry) => {
  const devengableLines = (entry?.entries || []).filter(isDevengableLine);
  if (!devengableLines.length) return null;

  const best = devengableLines.reduce((top, line) => {
    const amount = getLineAmount(line);
    if (!top || amount > getLineAmount(top)) return line;
    return top;
  }, null);

  return best ? { key: best.accountKey, name: best.accountName || '' } : null;
};

/** Split a total into n integer parts (CLP), putting the remainder on the last part. */
const splitAmount = (total, n) => {
  if (n <= 0) return [];
  const base = Math.floor(total / n);
  const parts = Array.from({ length: n }, () => base);
  parts[n - 1] = total - base * (n - 1);
  return parts;
};

const DevengarModal = ({ open, onClose, sourceEntry, onDevengado }) => {
  const { t } = useTranslation('accounting');
  const dispatch = useDispatch();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { reexecuteMutation: devengarEntry } = DevengarEntry();

  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [months, setMonths] = useState(1);
  const [gastoAccount, setGastoAccount] = useState(null);
  const [pasivoAccount, setPasivoAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset defaults whenever a new source entry / open.
  useEffect(() => {
    if (!open || !sourceEntry) return;
    setStartDate(sourceEntry.stringDate || moment().format('YYYY-MM-DD'));
    setMonths(1);
    setGastoAccount(getDefaultResultadoAccount(sourceEntry));
    setPasivoAccount(null);
    setIsLoading(false);
  }, [open, sourceEntry]);

  const devengableLines = useMemo(
    () => (sourceEntry?.entries || []).filter(isDevengableLine),
    [sourceEntry],
  );

  const devengableTotal = useMemo(
    () => getDevengableTotal(sourceEntry, gastoAccount?.key),
    [sourceEntry, gastoAccount?.key],
  );

  const invertAccrual = useMemo(
    () => shouldInvertAccrualForAccount(gastoAccount?.key, sourceEntry),
    [gastoAccount?.key, sourceEntry],
  );

  const n = Math.max(0, Math.floor(Number(months) || 0));

  // Build the N accrual draft entries (dates spaced monthly, amounts split equally).
  const drafts = useMemo(() => {
    if (!n || !devengableTotal || !moment(startDate, 'YYYY-MM-DD', true).isValid()) return [];
    const amounts = splitAmount(devengableTotal, n);
    return Array.from({ length: n }, (_, i) => {
      const d = moment(startDate, 'YYYY-MM-DD').add(i, 'months'); // moment clamps to month end
      const amount = amounts[i];
      return {
        stringDate: d.format('YYYY-MM-DD'),
        entryDate: {
          day: d.format('DD'),
          month: d.format('MM'),
          year: d.format('YYYY'),
          // 15:00 local to avoid timezone date-shift (mirrors createEntryDate).
          fullDate: new Date(d.year(), d.month(), d.date(), 15, 0, 0),
        },
        amount,
        lines: buildAccrualLines(
          amount,
          gastoAccount,
          pasivoAccount,
          t('devengar.pendingAccount'),
          invertAccrual,
        ),
      };
    });
  }, [n, devengableTotal, startDate, gastoAccount, pasivoAccount, invertAccrual, t]);

  // Preview objects shaped for EntryRow.
  const previewEntries = useMemo(
    () =>
      drafts.map((d, i) => ({
        id: `devengo-preview-${i}`,
        entryNumber: `${i + 1}/${n}`,
        stringDate: d.stringDate,
        entryDate: d.entryDate,
        entryType: 'freeEntry',
        currency: sourceEntry?.currency || 'CLP',
        description: t('devengar.draftDescription', {
          index: i + 1,
          total: n,
          base: (sourceEntry?.description || '').trim(),
        }),
        entries: d.lines,
      })),
    [drafts, n, sourceEntry, t],
  );

  const canConfirm =
    !isLoading &&
    n >= 1 &&
    devengableTotal > 0 &&
    !!gastoAccount?.key &&
    !!pasivoAccount?.key &&
    moment(startDate, 'YYYY-MM-DD', true).isValid();

  const currency = sourceEntry?.currency || 'CLP';
  const monthlyAmounts = useMemo(() => {
    if (!n || !devengableTotal) return [];
    return splitAmount(devengableTotal, n);
  }, [n, devengableTotal]);
  const perMonthLabel = useMemo(() => {
    if (!monthlyAmounts.length) return '';
    const first = monthlyAmounts[0];
    const last = monthlyAmounts[monthlyAmounts.length - 1];
    if (first === last) {
      return formatMoneyWithCurrency(first, currency);
    }
    return t('devengar.amountPerMonthRange', {
      first: formatMoneyWithCurrency(first, currency),
      last: formatMoneyWithCurrency(last, currency),
    });
  }, [monthlyAmounts, currency, t]);

  const handleConfirm = async () => {
    if (!canConfirm || !selectedCompany?.id || !sourceEntry) return;
    const sourceEntryId = String(sourceEntry.id || sourceEntry._id);
    const entries = drafts.map((d) => ({
      companyId: selectedCompany.id,
      description: t('devengar.draftDescription', {
        index: drafts.indexOf(d) + 1,
        total: n,
        base: (sourceEntry.description || '').trim(),
      }),
      stringDate: d.stringDate,
      entryDate: d.entryDate,
      entryType: 'freeEntry',
      currency: sourceEntry.currency || 'CLP',
      entries: d.lines.map((l) => ({
        ranking: l.ranking,
        accountKey: l.accountKey,
        accountName: l.accountName,
        debit: l.debit,
        credit: l.credit,
      })),
    }));

    setIsLoading(true);
    const result = await devengarEntry({
      input: { companyId: selectedCompany.id, sourceEntryId, entries },
    });
    setIsLoading(false);

    if (result.error) {
      const msg =
        result.error.graphQLErrors?.[0]?.message ||
        result.error.message ||
        t('devengar.error');
      dispatch(importAlertState({ state: 'error', content: msg, duration: 4 }));
      return;
    }

    dispatch(
      importAlertState({
        state: 'success',
        content: t('devengar.success', { count: n }),
        concept: t('devengar.successConcept'),
        duration: 4,
      }),
    );
    if (onDevengado) onDevengado();
    onClose();
  };

  const content = (
    <div className="devengar-modal-content">
      {sourceEntry && (
        <div className="devengar-modal-source">
          <div className="devengar-modal-source-header">
            <span className="devengar-modal-source-label">{t('devengar.sourceEntry')}</span>
            <span className="devengar-modal-source-number">#{sourceEntry.entryNumber}</span>
          </div>
          {devengableTotal > 0 && (
            <div className="devengar-modal-source-amount">
              <span className="devengar-modal-source-amount-label">{t('devengar.totalToAccrue')}</span>
              <span className="devengar-modal-source-amount-value">
                {formatMoneyWithCurrency(devengableTotal, currency)}
              </span>
            </div>
          )}
          <p className="devengar-modal-source-description">
            {(sourceEntry.description || '').trim() || t('devengar.noDescription')}
          </p>
        </div>
      )}

      <section className="devengar-modal-section">
        <h3 className="devengar-modal-section-title">{t('devengar.configSection')}</h3>
        <div className="devengar-modal-section-body">
          <div className="devengar-modal-row devengar-modal-row--params">
            <div className="journal-entry-field devengar-modal-field">
              <label className="journal-entry-label" htmlFor="devengar-start-date">
                {t('devengar.startDate')}
              </label>
              <input
                id="devengar-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="journal-entry-field devengar-modal-field devengar-modal-field--narrow">
              <label className="journal-entry-label" htmlFor="devengar-months">
                {t('devengar.months')}
              </label>
              <input
                id="devengar-months"
                type="number"
                min={1}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
              />
            </div>
          </div>

          <div className="devengar-modal-row">
            <div className="journal-entry-field devengar-modal-field">
              <label className="journal-entry-label">{t('devengar.gastoAccount')}</label>
              <PlanDeCuentaDropdown
                value={gastoAccount?.key || ''}
                onChange={(e, data) =>
                  setGastoAccount(data?.account ? { key: data.account.key, name: data.account.name } : null)
                }
                placeholder={t('devengar.selectAccount')}
                portalMenu
                menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
              />
            </div>
            <div className="journal-entry-field devengar-modal-field">
              <label className="journal-entry-label">{t('devengar.pasivoAccount')}</label>
              <PlanDeCuentaDropdown
                value={pasivoAccount?.key || ''}
                onChange={(e, data) =>
                  setPasivoAccount(data?.account ? { key: data.account.key, name: data.account.name } : null)
                }
                placeholder={t('devengar.selectAccount')}
                portalMenu
                menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
              />
            </div>
          </div>
        </div>
      </section>

      {devengableTotal > 0 && n >= 1 && (
        <div className="devengar-modal-stats" aria-label={t('devengar.summaryLabel')}>
          <div className="devengar-modal-stat">
            <span className="devengar-modal-stat-label">{t('devengar.entriesCount')}</span>
            <span className="devengar-modal-stat-value">{n}</span>
          </div>
          <div className="devengar-modal-stat">
            <span className="devengar-modal-stat-label">{t('devengar.amountPerMonth')}</span>
            <span className="devengar-modal-stat-value">{perMonthLabel}</span>
          </div>
        </div>
      )}

      {devengableLines.length === 0 ? (
        <div className="devengar-modal-warning">
          <span className="devengar-modal-warning-icon" aria-hidden>!</span>
          <span>{t('devengar.noGasto')}</span>
        </div>
      ) : devengableTotal <= 0 ? (
        <div className="devengar-modal-warning">
          <span className="devengar-modal-warning-icon" aria-hidden>!</span>
          <span>{t('devengar.noGastoAmount')}</span>
        </div>
      ) : (
        <section className="devengar-modal-section devengar-modal-section--preview">
          <div className="devengar-modal-section-heading">
            <h3 className="devengar-modal-section-title">{t('devengar.previewSection')}</h3>
            <span className="devengar-modal-preview-badge">{n}</span>
          </div>
          <div className="devengar-modal-preview-panel">
            <EntryRowList
              entries={previewEntries}
              classNamePrefix="entry"
              listClassName="devengar-modal-preview-list"
            />
          </div>
        </section>
      )}
    </div>
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={t('devengar.title')}
      classNameModal="devengar-modal"
      classNameContent="devengar-modal-content-wrapper"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      size="tiny"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        maxWidth: '95%',
        minWidth: '1300px',
        maxHeight: '90vh',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}
      content={content}
      confirmLabel={t('devengar.confirm')}
      onConfirm={handleConfirm}
      confirmDisabled={!canConfirm}
      isLoading={isLoading}
      cancelarLabel={t('devengar.cancel')}
    />
  );
};

export default DevengarModal;
